import { useFormikContext } from 'formik';
import { FC, useState } from 'react';
import { QcsTextField } from '../basic/QcsTextField';
import _ from 'lodash';
import { normalizeSpz } from '../../../utils/spzNormalization';
import { supplierCarService } from '@api/services/services';
import { SupplierCarDto } from '@api/models/suppliers/SupplierCarDto';
import QcsAlert from '../basic/QcsAlert';
import { useTranslation } from 'react-i18next';

interface Props {
  className?: string;
  fullWidth?: boolean;
  required?: boolean;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  checkForDuplications?: boolean;
  supplierId?: string;
}

export const SpzInput: FC<Props> = ({ className, fullWidth, required, name, label, disabled, autoFocus, onBlur, checkForDuplications, supplierId }) => {
  const { values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const [spzDuplications, setSpzDuplications] = useState<SupplierCarDto[]>([]);

  const { t } = useTranslation();

  const value = _.get(values, name);
  const wasTouched = !!submitCount || !!_.get(touched, name);
  const errorText = _.get(errors, name) as string;
  const hasError = !!errorText;


  const handleSpzBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const normalized = normalizeSpz(e.target.value);
    setFieldValue(name, normalized);
    handleBlur(e);
    onBlur?.(e);
    if (checkForDuplications) {
      const carsWithSpz = await supplierCarService.getSupplierCars({ spz: values.spz });
      const carDuplications = carsWithSpz.data.content.filter(
        (it) => it.supplier?.id !== supplierId && it.spz?.toLowerCase() === values.spz?.toLowerCase()
      );
      setSpzDuplications(carDuplications);
    }
  };

  return (
    <>
      <QcsTextField
        className={className}
        required={required}
        fullWidth={fullWidth}
        id={name}
        name={name}
        label={label ?? t('supplier.car.spz')}
        value={value === null || value === undefined ? '' : value}
        onChange={handleChange}
        onBlur={handleSpzBlur}
        error={wasTouched && hasError}
        helperText={wasTouched && errorText}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {spzDuplications.length === 1 && (
        <QcsAlert severity="warning">{t('supplier.car.spzDuplication', { supplier: spzDuplications[0].supplier?.name ?? '' })}</QcsAlert>
      )}
      {spzDuplications.length > 1 && (
        <QcsAlert severity="warning">
          {t('supplier.car.spzDuplications', { suppliers: spzDuplications.map((it) => it.supplier?.name).join(', ') })}
        </QcsAlert>
      )}
    </>
  );
};
